import { AttachmentCheckBox } from '@/components/create/letter/AttachmentCheckBox';
import { CreateFormAttachFile } from '@/components/create/letter/CreateFormAttachFile';
import { CrosswordCountSelectorField } from '@/components/common/field/CrosswordCountSelectorField';
import { Grid, Theme, Typography } from '@mui/material';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { SudokuCountSelectorField } from '@/components/common/field/SudokuCountSelectorField';
import { SudokuDifficulty } from '@/types/create/SudokuDifficulty';
import { SudokuDifficultySliderField } from '@/components/common/field/SudokuDifficultySliderField';
import { SudokuType } from '@/types/create/SudokuType';
import { SudokuTypeSelectorField } from '@/components/common/field/SudokuTypeSelectorField';
import { formatCurrency } from '@/utils/string-utils';
import React, { ChangeEvent, FC, ReactElement, useEffect } from 'react';

interface Props {
  photoCost: number;
  sudokuCost: number;
  crosswordCost: number;
  newsCost: number;
  allowAttachments?: boolean;
  allowSudoku?: boolean;
  allowCrossword?: boolean;
  allowNews?: boolean;
  photos: StagedPhotoDTO[];
  maxPhotoCount: number;
  isPhotoStaging?: boolean;
  onAttachPhoto: (file: File) => void;
  onRemovePhoto: (id: string) => void;
  sudokuAttached?: boolean;
  onSudokuAttached?: (e: ChangeEvent<HTMLInputElement>) => void;
  crosswordAttached?: boolean;
  onCrosswordAttached?: (e: ChangeEvent<HTMLInputElement>) => void;
  newsAttached?: boolean;
  onNewsAttached?: (e: ChangeEvent<HTMLInputElement>) => void;
  sportsNewsAttached?: boolean;
  onSportsNewsAttached?: (e: ChangeEvent<HTMLInputElement>) => void;
  sudokuCount: number;
  onSudokuCountChange: (sudokuCount: number) => void;
  sudokuType: SudokuType;
  sudokuTypeOptions: SudokuType[];
  onSudokuTypeChange: (sudokuType: SudokuType) => void;
  sudokuDifficulty: SudokuDifficulty;
  onSudokuDifficultyChange: (sudokuDifficulty: SudokuDifficulty) => void;
  crosswordCount: number;
  onCrosswordCountChange: (crosswordCount: number) => void;
  isMobile?: boolean;
}

export const CreateFormAttachments: FC<Props> = (props: Props): ReactElement => {
  const {
    photoCost,
    sudokuCost,
    crosswordCost,
    newsCost,
    allowAttachments,
    allowSudoku,
    allowCrossword,
    allowNews,
    photos,
    maxPhotoCount,
    isPhotoStaging,
    onAttachPhoto,
    onRemovePhoto,
    sudokuAttached,
    onSudokuAttached,
    sudokuCount,
    onSudokuCountChange,
    sudokuType,
    sudokuTypeOptions,
    onSudokuTypeChange,
    sudokuDifficulty,
    onSudokuDifficultyChange,
    crosswordAttached,
    onCrosswordAttached,
    crosswordCount,
    onCrosswordCountChange,
    newsAttached,
    onNewsAttached,
    sportsNewsAttached,
    onSportsNewsAttached,
    isMobile,
  } = props;

  // A previously selected (persisted) type may no longer be allowed by the server.
  useEffect(() => {
    if (!sudokuTypeOptions.includes(sudokuType)) {
      onSudokuTypeChange(sudokuTypeOptions[0]);
    }
  }, [sudokuTypeOptions, sudokuType, onSudokuTypeChange]);

  return (
    <Grid container={true} direction={'column'} alignItems={'start'} spacing={isMobile ? 1.5 : 2}>
      <Grid item={true} sx={{ width: '100%' }}>
        <CreateFormAttachFile
          attachmentCost={photoCost}
          maxPhotoCount={maxPhotoCount}
          uploadEnabled={allowAttachments}
          isPhotoStaging={isPhotoStaging}
          photos={photos}
          onAttachPhoto={onAttachPhoto}
          onRemovePhoto={onRemovePhoto}
        />
      </Grid>
      <Grid item={true}>
        <Grid container={true} direction={'row'} wrap={'wrap'} columnSpacing={6} rowSpacing={1.5}>
          <Grid item={true}>
            <Grid container={true} direction={'column'} spacing={2}>
              <Grid item={true}>
                <AttachmentCheckBox
                  label={`Судоку + ${formatCurrency(sudokuCost, 0)}`}
                  isUpdatable={false}
                  captionText={'Каждый раз уникальное'}
                  checked={sudokuAttached}
                  disabled={(!allowAttachments || !allowSudoku) && !sudokuAttached}
                  onChange={onSudokuAttached}
                />
              </Grid>
              {sudokuAttached && (
                <Grid item={true}>
                  <Grid container={true} direction={'row'} spacing={2} alignItems={'center'}>
                    <Grid item={true}>
                      <SudokuCountSelectorField
                        value={sudokuCount}
                        minValue={0}
                        maxValue={5}
                        step={1}
                        onValueChange={onSudokuCountChange}
                      />
                    </Grid>
                    <Grid item={true}>
                      <Typography variant={'h4'} minWidth={(theme: Theme): string => theme.spacing(8)}>
                        {`+ ${formatCurrency(sudokuCount * sudokuCost, 0)}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {sudokuAttached && (
                <Grid item={true} sx={{ mt: '10px' }}>
                  <Grid container={true} direction={'column'} spacing={4}>
                    {sudokuTypeOptions.length > 1 && (
                      <Grid item={true} sx={{ width: 246 }}>
                        <SudokuTypeSelectorField
                          value={sudokuType}
                          options={sudokuTypeOptions}
                          onValueChange={onSudokuTypeChange}
                        />
                      </Grid>
                    )}
                    <Grid item={true} sx={{ width: 246 }}>
                      <SudokuDifficultySliderField value={sudokuDifficulty} onValueChange={onSudokuDifficultyChange} />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item={true}>
            <Grid container={true} direction={'column'} spacing={2}>
              <Grid item={true}>
                <AttachmentCheckBox
                  label={`Кроссворд + ${formatCurrency(crosswordCost, 0)}`}
                  isUpdatable={false}
                  captionText={
                    <Typography variant={'caption'} component={'span'} maxWidth={'200px'} display={'flex'}>
                      {'В каждом десятки вопросов из разных областей'}
                    </Typography>
                  }
                  checked={crosswordAttached}
                  disabled={(!allowAttachments || !allowCrossword) && !crosswordAttached}
                  onChange={onCrosswordAttached}
                />
              </Grid>
              {crosswordAttached && (
                <Grid item={true}>
                  <Grid container={true} direction={'row'} spacing={2} alignItems={'center'}>
                    <Grid item={true}>
                      <CrosswordCountSelectorField
                        value={crosswordCount}
                        minValue={0}
                        maxValue={5}
                        step={1}
                        onValueChange={onCrosswordCountChange}
                      />
                    </Grid>
                    <Grid item={true}>
                      <Typography variant={'h4'} minWidth={(theme: Theme): string => theme.spacing(8)}>
                        {`+ ${formatCurrency(crosswordCount * crosswordCost, 0)}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item={true}>
            <AttachmentCheckBox
              label={`Главные новости + ${formatCurrency(newsCost, 0)}`}
              isUpdatable={false}
              captionText={'Свежие новости на момент отправки письма'}
              checked={newsAttached}
              disabled={(!allowAttachments || !allowNews) && !newsAttached}
              onChange={onNewsAttached}
            />
          </Grid>
          <Grid item={true}>
            <AttachmentCheckBox
              label={`Новости спорта + ${formatCurrency(newsCost, 0)}`}
              isUpdatable={false}
              captionText={'Актуальные спортивные новости'}
              checked={sportsNewsAttached}
              disabled={(!allowAttachments || !allowNews) && !sportsNewsAttached}
              onChange={onSportsNewsAttached}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
